import { SxProps, Theme } from '@mui/material';
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
} from '@tanstack/react-table';
import { RequestDocument, Variables } from 'graphql-request';
import { UseQueryObjectProps } from '../../hooks/queryObjectProps.types';
import { UseQuerySqlProps } from '../../hooks/useQuerySql.types';

export enum TableSizeEnum {
  xSmall = 'xSmall',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Tiles = 'tiles',
}

export enum FormatterType {
  boolean = 'bool',
  number = 'number',
  percentage = 'percentage',
  historicDate = 'historic-date',
  string = 'string',
  date = 'date',
  array = 'array',
  json = 'json',
  loading = 'loading',
}

export enum OptionQueryType {
  GQL = 'gql',
  SQL = 'sql',
  Object = 'queryObject',
  GQL_PAGINATION = 'gqlPagination',
}

type GqlAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { title: string; value: any }[];
};

type GqlPaginationAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { data: { title: string; value: any }[]; totals: number };
};

type SqlAsyncOptions = {
  type: OptionQueryType;
} & UseQuerySqlProps;

type QueryObjectAsyncOptions = {
  type: OptionQueryType;
} & UseQueryObjectProps;

export type AsyncGetOptions = (search: string) => GqlAsyncOptions | SqlAsyncOptions | QueryObjectAsyncOptions | GqlPaginationAsyncOptions;

export type DataRecord = Record<string, any>;

export type TActiveRowId = string | null;

export interface FilterConfig {
  options?: any[];
  asyncGetOptions?: AsyncGetOptions;
  getOptions?: (data: any, label: React.ReactNode, v) => any[];
  getFilterOptionFunc?: (row: any) => { value: any; title: string };
  type?: FormatterType;
}

export interface TColumnDefMeta {
  isKey?: boolean;
  hidden?: boolean;
  filter?: FilterConfig;
}

export type RequiredIdColumnDef<TData extends DataRecord> = Omit<ColumnDef<TData>, 'meta'> & {
  id: string;
  meta?: TColumnDefMeta;
};

export type TColumnsDef = RequiredIdColumnDef<DataRecord>[];

// make this deprecated when AvTableV2 effort is completed.
// /**
//  * @deprecated - Use {@link SortingState} instead
//  */
export type TOldSortingState = { property: string; isAsc?: boolean }[];

export interface IAvTableV2 {
  rows?: DataRecord[];
  headCells: TColumnsDef;
  loading?: boolean;
  totalRows?: number;
  storageKey?: string;
  multiSelect?: boolean;
  size?: TableSizeEnum;
  activeRowId?: TActiveRowId;
  onActiveRowIdChange?: OnChangeFn<TActiveRowId>;
  initialState?: Partial<TableState>;
  sorting?: SortingState;
  onSortingChange?: (newSorting: SortingState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (newPagination: PaginationState) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  enableActiveRowHighlighting?: boolean;
  sx?: SxProps<Theme>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  onIsAllRowsSelectedChange?: (isAllRowsSelected: boolean) => void;
  disableRowClickNavigation?: (dataRow: DataRecord) => boolean;
  inlineActions?: ((dataRow: DataRecord) => React.ReactNode)[];
  customActions?: ((selectedRowsIds: string[], selectedDataRows: DataRecord[]) => React.ReactNode)[];
  hasHeaderFilters?: boolean;
  allowKeyboardNavigation?: boolean;
  resizable?: boolean;
}
